import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../store';
import { fetchAssetGroupsInitialState } from './AssetGroupsService';
import {
  saveGroupsToLocalStorage,
  loadGroupsFromLocalStorage,
  clearGroupsFromLocalStorage,
} from './AssetGroupsPersistence';
import { updateCustomViewGroupTree, UpdateGroupParams } from './AssetGroupsTreeOperations';
import { findGroupByNormalizedName } from '../../utilities/groupUtils';

interface State {
  loading: boolean;
  groups: Array<Group> | null | undefined;
  searchGroups: Array<Group> | null | undefined;
  error: string | undefined;
  selectedAssetId: string | undefined;
  selectedAssetGuid: string | undefined;
  selectedAsset: Asset | undefined;
  assets: Array<Asset> | null | undefined;
  selectedGroup: Group | undefined;
  groupHashMap: Record<number, Group>;
  socketId: string | undefined;
  isGroupDrawerOpen: boolean;
  isNavigationDrawerCollapsed: boolean;
}

interface ActionPayload {
  groupId: number | undefined;
  assetId: string | undefined;
  searchString: string | undefined;
  assetIdGuid: string | undefined;
  showAssets?: boolean;
}

interface SocketActionPayload {
  socketId: string | undefined;
}

export interface Group {
  id: number;
  assetGroupName: string;
  childGroups: Array<Group>;
  assets: Array<Asset>;
  showAssets: boolean;
  class: string;
}

export interface Asset {
  assetId: string;
  assetName: string;
  industryApplicationId: number;
  wellOpType: number | null;
  enabled: boolean;
  groupName?: string;
  commStatus?: string;
  commsPercent?: number;
  runStatus?: string | number;
}

interface GroupResponse {
  groupName: string;
  childGroups: Array<GroupResponse>;
  assets: Array<AssetResponse>;
}

interface AssetResponse {
  assetId: string;
  assetName: string;
  industryApplicationId: number;
  wellOpType: number | null;
  enabled: boolean;
  groupName?: string;
  commStatus?: string;
  commsPercent?: number;
  runStatus?: string | number;
}

// Async thunk to fetch the initial state data
export const fetchInitialStateAsync = createAsyncThunk('assetGroupsSlice/fetchInitialState', async () => {
  // First check if we have valid persisted data
  const persistedData = loadGroupsFromLocalStorage();
  if (persistedData) {
    console.log('Loading asset groups from localStorage (temporary changes)');
    const groupHashMap = createGroupHashMap(persistedData.groups);
    const assets = Object.values(groupHashMap).flatMap((group) => group.assets);
    return { groups: persistedData.groups, groupHashMap, assets };
  }

  // If no persisted data, fetch from API
  const data = await fetchInitialState();
  const groupAndAssetData = data.values;
  const groups = mapGroupResponseToGroup(groupAndAssetData);
  const groupHashMap = createGroupHashMap(groups);
  const assets = Object.values(groupHashMap).flatMap((group) => group.assets);

  return { groups, groupHashMap, assets };
});

const createGroupHashMap = (groups: Group[]): Record<number, Group> => {
  const hashMap: Record<number, Group> = {};
  const addToHashMap = (group: Group) => {
    hashMap[group.id] = group;
    group.childGroups.forEach(addToHashMap);
  };
  groups.forEach(addToHashMap);
  return hashMap;
};

let groupId = 1;

const mapGroupResponseToGroup = (groups: GroupResponse[]): Group[] =>
  groups.map((group: GroupResponse) => ({
    id: groupId++,
    assetGroupName: group.groupName,
    childGroups: group.childGroups?.length ? mapGroupResponseToGroup(group.childGroups) : [],
    assets:
      group.assets?.map(
        (asset: AssetResponse): Asset => ({
          assetId: asset.assetId,
          assetName: asset.assetName,
          industryApplicationId: asset.industryApplicationId,
          wellOpType: asset.wellOpType,
          enabled: asset.enabled,
          groupName: group.groupName,
          commStatus: asset.commStatus,
          commsPercent: asset.commsPercent,
          runStatus: asset.runStatus,
        }),
      ) ?? [],
    showAssets: false,
    class: 'navigation-content-frame-item',
  }));

const initialState: State = {
  loading: false,
  groups: null,
  searchGroups: null,
  error: undefined,
  selectedAssetId: undefined,
  selectedAsset: undefined,
  assets: null,
  selectedGroup: undefined,
  groupHashMap: {},
  socketId: undefined,
  selectedAssetGuid: undefined,
  isGroupDrawerOpen: false,
  isNavigationDrawerCollapsed: false,
};

const updateGroupTree = (
  groups: Group[] | null | undefined,
  groupId: number,
  newGroup: Group,
  searchString: string,
): Group[] | null => {
  if (!groups) return null;

  return groups.map((group: Group) => {
    if (group.id === groupId) {
      return {
        ...newGroup,
        assets: searchString
          ? newGroup.assets.filter((asset) => asset.assetName.toLowerCase().includes(searchString))
          : newGroup.assets,
      } as Group;
    } else {
      return {
        ...group,
        childGroups: updateGroupTree(group.childGroups, groupId, newGroup, searchString),
      } as Group;
    }
  });
};

const resetShowAssets = (groups: Group[] | null | undefined): Group[] | null => {
  if (!groups) return null;

  return groups.map((group: Group) => {
    return {
      ...group,
      showAssets: false,
      childGroups: resetShowAssets(group.childGroups),
      class: 'navigation-content-frame-item',
    } as Group;
  });
};

const setGroupHashMapDisplayState = (groupHashMap: Record<number, Group>, active: boolean): Record<number, Group> => {
  const resetGroupDisplayState = (group: Group): Group => ({
    ...group,
    showAssets: active,
    class: active ? 'navigation-content-frame-item item-selected' : 'navigation-content-frame-item',
    childGroups: group.childGroups.map(resetGroupDisplayState),
  });

  const newHashMap = { ...groupHashMap };
  Object.keys(newHashMap).forEach((key) => {
    newHashMap[key as any] = resetGroupDisplayState(newHashMap[key as any]);
  });
  return newHashMap;
};

export const assetGroupsSlice = createSlice({
  name: 'assetGroups',
  initialState,
  reducers: {
    setInitialState: () => {
      return initialState;
    },
    setGroupDrawerState: (state, action: PayloadAction<boolean>) => {
      state.isGroupDrawerOpen = action.payload;
    },
    setNavigationDrawerCollapsed: (state, action: PayloadAction<boolean>) => {
      state.isNavigationDrawerCollapsed = action.payload;
    },
    updateAssetsDisplayState: (state, action: PayloadAction<ActionPayload>) => {
      const id = action.payload.groupId;
      if (!id) {
        return;
      }

      const group = state.groupHashMap[id];

      if (group) {
        // Use explicit showAssets if provided, otherwise toggle
        const showAssets = action.payload.showAssets !== undefined ? action.payload.showAssets : !group.showAssets;
        state.groupHashMap[id] = {
          ...group,
          showAssets,
          class: 'navigation-content-frame-item',
        };

        const searchString = action.payload.searchString?.toLowerCase() || '';
        state.groups = updateGroupTree(state.groups, id, state.groupHashMap[id], searchString);
        state.searchGroups = updateGroupTree(state.searchGroups, id, state.groupHashMap[id], searchString);
      }
    },
    updateSelectedAsset: (state, action: PayloadAction<ActionPayload>) => {
      state.selectedAssetId = action.payload.assetId;
      // First try to find by GUID if provided, otherwise fall back to finding by asset name
      if (action.payload.assetIdGuid) {
        state.selectedAsset = state.assets?.find(
          (asset) => asset.assetId?.toLowerCase() === action.payload.assetIdGuid?.toLowerCase(),
        );
      } else {
        state.selectedAsset = state.assets?.find((asset) => asset.assetName === action.payload.assetId);
      }
      state.selectedAssetGuid = action.payload.assetIdGuid;

      if (action.payload.groupId) {
        if (state.selectedGroup?.id != action.payload.groupId) {
          state.selectedGroup = state.groupHashMap[action.payload.groupId];
        }
      } else {
        state.selectedGroup = undefined;
      }
    },
    setSelectedGroupByName: (state, action: PayloadAction<{ groupName: string }>) => {
      if (state.groups && action.payload.groupName) {
        const foundGroup = findGroupByNormalizedName(state.groups, action.payload.groupName);
        state.selectedGroup = foundGroup;
      } else {
        state.selectedGroup = undefined;
      }
    },
    setSelectedAsset: (state, action: PayloadAction<{ assetId: string }>) => {
      if (state.assets && action.payload.assetId) {
        const foundAsset =
          state.assets.find((asset) => asset.assetId === action.payload.assetId) ||
          state.assets.find((asset) => asset.assetName === action.payload.assetId);

        state.selectedAsset = foundAsset;
        state.selectedAssetId = foundAsset?.assetName || action.payload.assetId;
        state.selectedAssetGuid = foundAsset?.assetId;
      } else {
        state.selectedAsset = undefined;
        state.selectedAssetId = undefined;
        state.selectedAssetGuid = undefined;
      }
    },
    updateSocketId: (state, action: PayloadAction<SocketActionPayload>) => {
      state.socketId = action.payload.socketId;
    },
    updateSearchGroups: (state, action: PayloadAction<ActionPayload>) => {
      const searchString = action.payload.searchString?.toLowerCase();
      state.searchGroups = resetShowAssets(state.groups);
      if (searchString) {
        state.groupHashMap = setGroupHashMapDisplayState(state.groupHashMap, true);

        const allGroups = Object.values(state.groupHashMap);
        state.searchGroups = allGroups
          .filter((group: Group) => group.assets.some((asset) => asset.assetName.toLowerCase().includes(searchString)))
          .map((group: Group) => ({
            ...group,
            showAssets: true,
            assets: group.assets.filter((asset) => asset.assetName.toLowerCase().includes(searchString)),
          }));
      } else {
        state.groupHashMap = setGroupHashMapDisplayState(state.groupHashMap, false);
      }
    },
    updateCustomViewGroup: (state, action: PayloadAction<UpdateGroupParams>) => {
      const params = action.payload;

      // Use the tree operations utility to handle the complex logic
      const result = updateCustomViewGroupTree(state.groups, state.groupHashMap, state.selectedGroup, params);

      // Update state with the results
      state.groups = result.updatedGroups;
      state.searchGroups = result.updatedGroups;

      // Rebuild hash map
      if (state.groups) {
        state.groupHashMap = createGroupHashMap(state.groups);

        // Save changes to localStorage with timestamp
        saveGroupsToLocalStorage({
          groups: state.groups,
          timestamp: Date.now(),
        });

        // Update selected group if it was modified
        if (result.updatedSelectedGroup !== undefined) {
          if (result.updatedSelectedGroup === null) {
            // Find the updated group in the new hash map
            const updatedSelectedGroup = Object.values(state.groupHashMap).find(
              (group) => group.assetGroupName === params.newGroupName,
            );
            state.selectedGroup = updatedSelectedGroup;
          } else {
            state.selectedGroup = result.updatedSelectedGroup;
          }
        }
      }
    },
    clearTemporaryChanges: () => {
      // Clear localStorage data - typically called after successful server sync
      clearGroupsFromLocalStorage();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInitialStateAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInitialStateAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload.groups;
        state.groupHashMap = action.payload.groupHashMap;
        state.searchGroups = action.payload.groups;
        state.assets = action.payload.assets;
      })
      .addCase(fetchInitialStateAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const fetchInitialState = async () => {
  try {
    const response = await fetchAssetGroupsInitialState();
    return response;
  } catch (error) {
    console.error('Error fetching initial state:', error);
    throw error;
  }
};

// Export the actions from the itemsSlice
export const {
  updateAssetsDisplayState,
  updateSelectedAsset,
  updateSearchGroups,
  setSelectedGroupByName,
  setSelectedAsset,
  setInitialState,
  updateSocketId,
  setGroupDrawerState,
  setNavigationDrawerCollapsed,
  updateCustomViewGroup,
  clearTemporaryChanges,
} = assetGroupsSlice.actions;

export const selectAssetGroups = (state: RootState) => state.assetGroups;

export default assetGroupsSlice.reducer;
